import {
  signInWithPopup,
  signOut as fbSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  Unsubscribe,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, googleProvider, db } from "../lib/firebase";
import { UserProfile, UserRole, UserStatus } from "../types";
import { DEFAULT_STAFF_ACCESS } from "./settingsService";

export const ALLOWED_DOMAIN = "mlconnections.com";

/**
 * Validates if an email address belongs to the allowed MLC Google Workspace domain.
 */
export function isAllowedDomain(email: string | null | undefined): boolean {
  if (!email) return false;
  return email.toLowerCase().endsWith(`@${ALLOWED_DOMAIN}`);
}

/**
 * Determines the initial role and access status for an email using staffAccess or defaults
 */
export async function resolveInitialUserAccess(
  email: string,
): Promise<{ role: UserRole; status: UserStatus }> {
  const normalized = email.trim().toLowerCase();

  try {
    const settingsSnap = await getDoc(doc(db, "settings", "app_settings"));
    if (settingsSnap.exists()) {
      const data = settingsSnap.data();
      const staffAccess = data?.staffAccess || {};
      const config = staffAccess[normalized];
      if (config) {
        return {
          role: config.role || "contributor",
          status: config.hasAccess ? "active" : "inactive",
        };
      }
    }
  } catch (err) {
    console.warn("Could not check staffAccess during initial auth setup:", err);
  }

  // Check known default team members
  const defaultConfig = DEFAULT_STAFF_ACCESS[normalized];
  if (defaultConfig) {
    return {
      role: defaultConfig.role,
      status: defaultConfig.hasAccess ? "active" : "inactive",
    };
  }

  // Non-authorized staff member
  return {
    role: "viewer",
    status: "inactive",
  };
}

/**
 * Initiates Google SSO and syncs the user profile into mlc-proposal-hq-db
 */
export async function signInWithGoogle(): Promise<UserProfile> {
  const result = await signInWithPopup(auth, googleProvider);
  const fbUser = result.user;

  if (!isAllowedDomain(fbUser.email)) {
    await fbSignOut(auth);
    throw new Error(
      `Access restricted. Please sign in with your @${ALLOWED_DOMAIN} company Google account.`,
    );
  }

  const userRef = doc(db, "users", fbUser.uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return userSnap.data() as UserProfile;
  }

  const access = await resolveInitialUserAccess(fbUser.email || "");

  const newProfile: UserProfile = {
    uid: fbUser.uid,
    email: fbUser.email || "",
    displayName: fbUser.displayName || fbUser.email?.split("@")[0] || "MLC Team Member",
    photoURL: fbUser.photoURL,
    role: access.role,
    status: access.status,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  await setDoc(userRef, {
    ...newProfile,
    serverCreatedAt: serverTimestamp(),
    serverUpdatedAt: serverTimestamp(),
  });

  return newProfile;
}

/**
 * Signs the user out of Firebase
 */
export async function signOut(): Promise<void> {
  await fbSignOut(auth);
}

/**
 * Subscribes to Firebase auth state changes and syncs user profile
 */
export function subscribeToAuth(
  callback: (user: UserProfile | null, loading: boolean) => void,
): Unsubscribe {
  return onAuthStateChanged(auth, async (fbUser: FirebaseUser | null) => {
    if (!fbUser || !isAllowedDomain(fbUser.email)) {
      callback(null, false);
      return;
    }

    try {
      const userRef = doc(db, "users", fbUser.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        callback(userSnap.data() as UserProfile, false);
      } else {
        const access = await resolveInitialUserAccess(fbUser.email || "");
        const initialProfile: UserProfile = {
          uid: fbUser.uid,
          email: fbUser.email || "",
          displayName: fbUser.displayName || fbUser.email?.split("@")[0] || "MLC Team Member",
          photoURL: fbUser.photoURL,
          role: access.role,
          status: access.status,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        await setDoc(userRef, initialProfile);
        callback(initialProfile, false);
      }
    } catch {
      callback(null, false);
    }
  });
}
